import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft } from 'lucide-react';
import SacredSpaceTab from '../../components/SacredSpaceTab';

const CHAMBER_IMAGE = 'https://res.cloudinary.com/dpppkdhdk/image/upload/v1775736500/Templo_celestial_de_luz_dorada_zizvwv.png';

/* Golden dust particles descending from dome */
function DomeParticles() {
  const particles = Array.from({ length: 12 }, (_, i) => ({
    x: 35 + (i * 3.7 % 30),
    delay: (i * 1.8) % 14,
    dur: 10 + (i % 4) * 3,
    size: 3 + (i % 3) * 1.5,
  }));

  return (
    <>
      <style>{`
        @keyframes dustFall {
          0% { opacity: 0; transform: translateY(0) }
          10% { opacity: .7 }
          50% { opacity: .3 }
          90% { opacity: .5 }
          100% { opacity: 0; transform: translateY(35vh) }
        }
      `}</style>
      {particles.map((p, i) => (
        <div key={`dp${i}`} style={{
          position: 'absolute',
          left: `${p.x}%`, top: '5%',
          width: `${p.size}px`, height: `${p.size}px`,
          borderRadius: '50%',
          background: '#fffae0',
          boxShadow: `0 0 ${p.size * 2}px ${p.size}px rgba(212,175,55,.4)`,
          animation: `dustFall ${p.dur}s ease-in-out infinite`,
          animationDelay: `${p.delay}s`,
        }} />
      ))}
    </>
  );
}

const CamaraSagrada = ({ onBack }) => {
  const [enteredAt] = useState(Date.now());
  const [minutes, setMinutes] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [leaving, setLeaving] = useState(false);

  /* Timer — counts minutes spent in the chamber */
  useEffect(() => {
    setFadeIn(true);
    const interval = setInterval(() => {
      setMinutes(Math.floor((Date.now() - enteredAt) / 60000));
    }, 30000);
    return () => clearInterval(interval);
  }, [enteredAt]);

  const handleExit = useCallback(() => {
    setLeaving(true);
    setTimeout(() => onBack(), 1500);
  }, [onBack]);

  return (
    <div className="relative w-full h-screen overflow-hidden" data-testid="camara-sagrada" style={{ background: '#0a0a12' }}>
      <style>{`
        @keyframes geoRotate { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        @keyframes geoBreathe {
          0%, 100% { transform: scale(0.97) }
          50% { transform: scale(1.03) }
        }
        @keyframes crystalPulse {
          0%, 100% { opacity: .2 }
          50% { opacity: .55 }
        }
        @keyframes domeLight {
          0%, 100% { opacity: .15 }
          50% { opacity: .4 }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px) }
          to { opacity: 1; transform: translateY(0) }
        }
        @keyframes selectorIn {
          from { opacity: 0; transform: scale(.85) }
          to { opacity: 1; transform: scale(1) }
        }
      `}</style>

      {/* Entry / Exit transition */}
      <div className={`absolute inset-0 z-50 pointer-events-none transition-opacity duration-[1500ms] ${
        !fadeIn || leaving ? 'opacity-100' : 'opacity-0'
      }`} style={{ background: '#faf8f4' }} />

      {/* Background image — sacred chamber */}
      <img
        src={CHAMBER_IMAGE}
        alt="Cámara Sagrada del Corazón"
        className="w-full h-full object-cover"
        style={{ objectPosition: 'center 40%' }}
        draggable={false}
        data-testid="chamber-bg"
      />

      {/* Effects layer */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        {/* Dome light beam */}
        <div style={{
          position: 'absolute',
          left: '45%', top: '0',
          width: '10%', height: '45%',
          background: 'linear-gradient(to bottom, rgba(255,250,230,.2), transparent)',
          animation: 'domeLight 8s ease-in-out infinite',
        }} />

        {/* Crystal base glow — left */}
        <div style={{
          position: 'absolute',
          left: '8%', bottom: '8%',
          width: '14px', height: '14px', borderRadius: '50%',
          background: '#fffae0',
          boxShadow: '0 0 40px 25px rgba(212,175,55,.2), 0 0 80px 50px rgba(212,175,55,.08)',
          animation: 'crystalPulse 7s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute',
          left: '15%', bottom: '12%',
          width: '10px', height: '10px', borderRadius: '50%',
          background: '#fffae0',
          boxShadow: '0 0 30px 18px rgba(212,175,55,.15), 0 0 60px 35px rgba(212,175,55,.06)',
          animation: 'crystalPulse 7s ease-in-out infinite',
          animationDelay: '2s',
        }} />

        {/* Crystal base glow — right */}
        <div style={{
          position: 'absolute',
          right: '8%', bottom: '8%',
          width: '14px', height: '14px', borderRadius: '50%',
          background: '#fffae0',
          boxShadow: '0 0 40px 25px rgba(212,175,55,.2), 0 0 80px 50px rgba(212,175,55,.08)',
          animation: 'crystalPulse 7s ease-in-out infinite',
          animationDelay: '1s',
        }} />
        <div style={{
          position: 'absolute',
          right: '15%', bottom: '12%',
          width: '10px', height: '10px', borderRadius: '50%',
          background: '#fffae0',
          boxShadow: '0 0 30px 18px rgba(212,175,55,.15), 0 0 60px 35px rgba(212,175,55,.06)',
          animation: 'crystalPulse 7s ease-in-out infinite',
          animationDelay: '3.5s',
        }} />

        {/* Golden dust from dome */}
        <DomeParticles />
      </div>

      {/* Time counter — very subtle, top center */}
      {minutes > 0 && (
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20 pointer-events-none"
          style={{ animation: 'fadeUp .8s ease-out both' }}>
          <p className="text-white/30 text-xs tracking-[0.15em] font-light" data-testid="time-counter">
            {minutes} {minutes === 1 ? 'minuto' : 'minutos'} en tu centro
          </p>
        </div>
      )}

      {/* Sacred Space Tab — left side expandable panel */}
      <SacredSpaceTab spaceId="templo_corazon" theme="dark" />

      {/* Back button — top left, offset for tab */}
      <div className="absolute top-4 left-16 z-40">
        <button
          onClick={handleExit}
          className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-sm bg-black/30 backdrop-blur-md rounded-full px-3 py-1.5"
          data-testid="chamber-exit-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
      </div>

    </div>
  );
};

export default CamaraSagrada;
